/**
 * MetaRow molecule — a single dot-joined caption line (e.g. club · age · contract).
 *
 * Atoms only, semantic tokens. Filters empty/nullish segments and joins with
 * " · ". Truncates to one line. Renders nothing when no segment is present.
 * @module components/molecules/MetaRow/MetaRowView
 */
import React, { memo } from "react";

import { Text } from "@/components/atoms/Text";

/** Props for MetaRowView */
interface MetaRowViewProps {
  /** Ordered segments; null/undefined/empty are dropped before dot-joining */
  readonly segments: readonly (string | number | null | undefined)[];
  /** Optional testID forwarded to the text node */
  readonly testID?: string;
}

/**
 * Dot-joins the non-empty segments.
 * @param segments - Ordered segments (null/undefined/empty dropped)
 * @returns The " · "-joined string (empty when no segment remains)
 */
const joinSegments = (
  segments: readonly (string | number | null | undefined)[]
): string =>
  segments
    .filter(
      (s): s is string | number => s !== null && s !== undefined && s !== ""
    )
    .join(" · ");

/**
 * Renders the dot-joined meta line, or null when empty.
 * @param root0 - Component props
 * @param root0.segments - Ordered segments to dot-join
 * @param root0.testID - Optional testID
 */
const MetaRowView = ({ segments, testID }: MetaRowViewProps) =>
  joinSegments(segments) ? (
    <Text
      testID={testID}
      variant="caption"
      className="flex-1 text-content-secondary"
      numberOfLines={1}
    >
      {joinSegments(segments)}
    </Text>
  ) : null;

MetaRowView.displayName = "MetaRowView";

export default memo(MetaRowView);
